import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface SharedUser {
  id: string;
  display_name: string | null;
  email: string;
  avatar_url: string | null;
  permission_level: 'view' | 'edit';
  shared_at: string;
}

// Hook para contar TODOS os compartilhamentos do usuário (global)
export function useGlobalShareCount() {
  return useQuery({
    queryKey: ['global-share-count'],
    queryFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return 0;

      const { count: casoSharesCount } = await supabase
        .from('caso_user_shares')
        .select('*', { count: 'exact', head: true })
        .eq('shared_by_user_id', userData.user.id);

      const { count: chatSharesCount } = await supabase
        .from('conversation_user_shares')
        .select('*', { count: 'exact', head: true })
        .eq('shared_by_user_id', userData.user.id);

      return (casoSharesCount || 0) + (chatSharesCount || 0);
    },
  });
}

// 1. Buscar usuários para compartilhar (validação de email completo)
export function useSearchUsers(searchTerm: string) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  return useQuery({
    queryKey: ['search-users', searchTerm],
    queryFn: async () => {
      // Validar se é um e-mail completo e válido
      if (!searchTerm || !emailRegex.test(searchTerm)) {
        return [];
      }
      
      const { data: userData } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('users')
        .select('id, display_name, email, avatar_url')
        .eq('email', searchTerm.toLowerCase())
        .neq('id', userData.user?.id)
        .limit(1);

      if (error) throw error;
      return data;
    },
    enabled: emailRegex.test(searchTerm),
  });
}

// 2. Buscar usuários com acesso ao caso
export function useSharedUsers(casoId: string) {
  return useQuery<SharedUser[]>({
    queryKey: ['shared-users', casoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('caso_user_shares')
        .select(`
          permission_level,
          shared_at,
          shared_with_user_id,
          users!caso_user_shares_shared_with_user_id_fkey(
            id,
            display_name,
            email,
            avatar_url
          )
        `)
        .eq('caso_id', casoId);

      if (error) throw error;
      
      return (data || []).map((share: any) => ({
        id: share.users.id,
        display_name: share.users.display_name,
        email: share.users.email,
        avatar_url: share.users.avatar_url,
        permission_level: share.permission_level,
        shared_at: share.shared_at,
      }));
    },
    enabled: !!casoId,
  });
}

// 3. Compartilhar caso com usuários
export function useShareCasoMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      casoId,
      userIds,
      permissionLevel = 'view',
    }: {
      casoId: string;
      userIds: string[];
      permissionLevel?: 'view' | 'edit';
    }) => {
      // ✅ VERIFICAÇÃO EXPLÍCITA DE SESSÃO
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Sessão expirada. Faça login novamente.');
      }

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Usuário não autenticado');
      
      const currentUserId = userData.user.id;

      // ✅ Buscar plano do usuário — mais recente ativa
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('plan_type')
        .eq('user_id', currentUserId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const planType = subscription?.plan_type || 'free';

      // ✅ Definir limites de compartilhamento por plano
      const shareLimits: Record<string, number> = {
        free: 0,
        basic: 1,
        pro: 10,
        premium: -1, // ilimitado
      };

      const maxShares = shareLimits[planType] ?? 0;

      // ✅ Buscar TODOS os compartilhamentos do usuário (casos + chats)
      const { count: casoSharesCount } = await supabase
        .from('caso_user_shares')
        .select('*', { count: 'exact', head: true })
        .eq('shared_by_user_id', currentUserId);

      const { count: chatSharesCount } = await supabase
        .from('conversation_user_shares')
        .select('*', { count: 'exact', head: true })
        .eq('shared_by_user_id', currentUserId);

      const totalCurrentShares = (casoSharesCount || 0) + (chatSharesCount || 0);
      const totalAfterShare = totalCurrentShares + userIds.length;

      // ✅ Validar limites
      if (maxShares === 0) {
        throw new Error('Seu plano não permite compartilhamento. Faça upgrade para o plano Básico ou superior.');
      }

      if (maxShares > 0 && totalAfterShare > maxShares) {
        throw new Error(
          `Limite de compartilhamento atingido!\n\n` +
          `📊 Uso atual: ${totalCurrentShares}/${maxShares}\n` +
          `➕ Tentando adicionar: ${userIds.length}\n` +
          `🚫 Total seria: ${totalAfterShare}\n\n` +
          `Seu plano ${planType.toUpperCase()} permite ${maxShares} compartilhamento(s) no total (casos + chats).\n\n` +
          `Atualize para o plano Pro (10) ou Premium (ilimitado).`
        );
      }

      const shares = userIds.map(userId => ({
        caso_id: casoId,
        shared_with_user_id: userId,
        shared_by_user_id: currentUserId,
        permission_level: permissionLevel,
      }));

      const { error } = await supabase
        .from('caso_user_shares')
        .insert(shares);

      if (error) throw error;

      // Get caso details
      const { data: casoData } = await supabase
        .from('casos')
        .select('title')
        .eq('id', casoId)
        .single();

      // Log activity in firm_activity_log
      const activityLogs = userIds.map(userId => ({
        firm_id: '00000000-0000-0000-0000-000000000000',
        event_type: 'case_shared_user',
        actor_id: currentUserId,
        resource_type: 'case',
        resource_id: casoId,
        target_id: userId,
        metadata: {
          caso_title: casoData?.title || 'Caso sem título',
          permission_level: permissionLevel,
        },
      }));

      await supabase.from('firm_activity_log').insert(activityLogs);

      // Get sender profile
      const { data: senderProfile } = await supabase
        .from('users')
        .select('display_name')
        .eq('id', currentUserId)
        .single();

      const senderName = senderProfile?.display_name || 'Alguém';
      const casoTitle = casoData?.title || 'um caso';

      // Buscar expires_at do primeiro share criado
      const { data: shareData } = await supabase
        .from('caso_user_shares')
        .select('expires_at')
        .eq('caso_id', casoId)
        .eq('shared_by_user_id', currentUserId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // Create notifications com client oficial
      const notificationInserts = userIds.map(userId => ({
        type: 'caso_shared',
        title: 'Novo caso compartilhado',
        message: `${senderName} compartilhou "${casoTitle}" com você`,
        target_user_id: userId,
        sent_by: currentUserId, // ✅ Casa com policy sent_by = auth.uid()
        metadata: { 
          caso_id: casoId,
          expires_at: shareData?.expires_at || null
        },
      }));

      console.log('📧 Tentando criar notificações:', notificationInserts);

      const { data: notifData, error: notifError } = await supabase
        .from('notifications')
        .insert(notificationInserts)
        .select();

      if (notifError) {
        console.error('❌ Erro ao criar notificações:', notifError);
        console.error('Details:', JSON.stringify(notifError, null, 2));
        toast.error(`Caso compartilhado, mas notificação falhou: ${notifError.message}`);
      } else {
        console.log('✅ Notificações criadas com sucesso:', notifData);
      }

      return shares;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shared-users', variables.casoId] });
      queryClient.invalidateQueries({ queryKey: ['casos'] });
      queryClient.invalidateQueries({ queryKey: ['global-share-count'] });
      toast.success(`Caso compartilhado com ${variables.userIds.length} usuário(s)`);
    },
    onError: (error: any) => {
      if (error.message?.includes('duplicate')) {
        toast.error('Um ou mais usuários já têm acesso a este caso');
      } else {
        toast.error('Erro ao compartilhar caso');
      }
    },
  });
}

// 4. Remover compartilhamento
export function useUnshareCasoMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      casoId,
      userId,
    }: {
      casoId: string;
      userId: string;
    }) => {
      const { error } = await supabase
        .from('caso_user_shares')
        .delete()
        .eq('caso_id', casoId)
        .eq('shared_with_user_id', userId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shared-users', variables.casoId] });
      queryClient.invalidateQueries({ queryKey: ['casos'] });
      queryClient.invalidateQueries({ queryKey: ['global-share-count'] });
      toast.success('Acesso removido com sucesso');
    },
    onError: () => {
      toast.error('Erro ao remover acesso');
    },
  });
}
